import { ForwardedRef, forwardRef } from 'react';
import { SelectProps, Stack } from '@heathmont/moon-components';
import useTranslation from '~/client/hooks/i18n/useTranslation';
import CustomSelect from './CustomSelect';
import FormError from '../FormError';

type Props = SelectProps & {
  error?: string;
  maxWidth?: string;
  ref?: ForwardedRef<HTMLSelectElement>;
  options?: SelectOption[];
  secondaryOptions?: SelectOption[];
  secondaryOptionsLabel?: string;
  enableEmptyValue?: boolean;
  skipErrorMessage?: boolean;
};

const FormSelect = forwardRef<HTMLSelectElement, Props>((props, ref) => {
  const {
    name,
    options,
    secondaryOptions = [],
    secondaryOptionsLabel = '',
    error = '',
    label = '',
    placeholder = '',
    enableEmptyValue = false,
    skipErrorMessage = false,
    ...selectProps
  } = props;
  const labelTranslation = useTranslation(label);
  const placeholderTranslation = useTranslation(placeholder);
  return (
    <Stack space="0">
      <CustomSelect
        name={name}
        label={labelTranslation}
        ref={ref}
        placeholder={placeholderTranslation}
        error={!!error}
        {...selectProps}
      >
        <option value="" disabled={!enableEmptyValue} selected>
          {placeholderTranslation}
        </option>

        {options?.map(({ label: optionLabel, value }) => (
          <option key={value} label={optionLabel} value={value}>
            {optionLabel}
          </option>
        ))}

        {secondaryOptions && secondaryOptions.length > 0 && (
          <optgroup label={secondaryOptionsLabel}>
            {secondaryOptions.map(({ label: optionLabel, value }) => (
              <option key={value} label={optionLabel} value={value}>
                {optionLabel}
              </option>
            ))}
          </optgroup>
        )}
      </CustomSelect>
      {!skipErrorMessage && (
        <FormError message={error} data-testid={`${name}-form-error`} />
      )}
    </Stack>
  );
});

export default FormSelect;
